import type { SubscriptionIssues } from "~services/app/endpointGuards";
import type { ServerPrefix, VerbKeywords } from "~services/app/endpointNames";
import type { AppApiRequests as ApiRequests, AppApiResponses as ApiResponses } from "~services/app/frontendApi";

import * as Sentry from "@sentry/browser";
import axios from "axios";
import DataTypes from "~data";
import { EndpointAuth } from "~services/app/endpointGuards";

import { makeApiErrorEvent } from "./api-errors";
import { getRouteBaseHostname } from "./paths";
import { makeNewerApiVersionInteractionEvent, makeOlderApiVersionInteractionEvent } from "./providers/ApiCacheProvider";
import { ContainsAllCheck, containsFile, isPlainObject, presence, sleep, uuid } from "./utils";
import Day from "./utils/Day";
import getRecaptchaToken, { REQUIRED_CAPTCHA_ENDPOINTS } from "./utils/recaptcha";

let failingSince: Date | undefined = undefined,
  mfaCode: string | undefined = undefined,
  mfaCodePromise: Promise<void> | undefined = undefined;

const authenticationToken = location.pathname.startsWith("/embed")
  ? localStorage.getItem("upscope:authentication-token")
  : null;

const verbKeywords = {
  delete: ["delete"] as const,
  get: ["show", "list", "search", "download"] as const,
  post: ["create", "do", "upload", "send"] as const,
  put: ["update", "toggle", "add"] as const,
} as const;

const rateLimitErrors = [429, 502, 503, 504] as const;

const getVerbKeywordsCheck: ContainsAllCheck<VerbKeywords["get"], (typeof verbKeywords)["get"][number]> = true;
const postVerbKeywordsCheck: ContainsAllCheck<VerbKeywords["post"], (typeof verbKeywords)["post"][number]> = true;
const putVerbKeywordsCheck: ContainsAllCheck<VerbKeywords["put"], (typeof verbKeywords)["put"][number]> = true;
const deleteVerbKeywordsCheck: ContainsAllCheck<VerbKeywords["delete"], (typeof verbKeywords)["delete"][number]> = true;

const serverPrefixes = {
  auth: "*",
  global: "*",
  hq: "hq",
  hs: "hs",
  up: "up",
} as const satisfies ServerPrefix;

const axiosClient = axios.create({
  baseURL: "/",
  headers: {
    "Accept": "application/json",
    "Content-Type": "application/json",
    "X-Client-Timezone": timezone(),
    "X-Integration-Authentication-Token": authenticationToken,
    "X-Version": process.env.CURRENT_APP_VERSION,
  },
  validateStatus: (status) => status < 502 || rateLimitErrors.includes(status),
});

export type EndpointsWithoutData = {
  [key in keyof ApiRequests]: ApiRequests[key] extends undefined ? key : never;
}[keyof ApiRequests];

export type EndpointsWithRequiredData = {
  [key in keyof ApiRequests]: ApiRequests[key] extends undefined ? never : key;
}[keyof ApiRequests];

type RequestHeaders = {
  "Content-Type"?: string;
  "X-Captcha-Token"?: string;
  "X-Mfa-Code"?: string;
  "X-Request-Id"?: string;
};

export default function api<T extends EndpointsWithRequiredData>(
  endpoint: T,
  data: ApiRequests[T],
): Promise<ApiResponses[T]>;
export default function api<T extends EndpointsWithoutData>(endpoint: T): Promise<ApiResponses[T]>;
export default async function api<T extends keyof ApiRequests>(
  endpoint: T,
  data?: ApiRequests[T],
): Promise<ApiResponses[T]> {
  return apiRequest(endpoint as any, data as any) as any;
}

export function deserializeDates(object?: undefined): undefined;

export function deserializeDates(object?: null): null;

export function deserializeDates(object?: Record<string, any>): Record<string, any>;

export function deserializeDates(object?: null | Record<string, any>): null | Record<string, any> | undefined {
  if (!isPlainObject(object)) return object;

  const objectCopy = { ...object };
  for (const key in objectCopy) {
    if (Array.isArray(objectCopy[key])) {
      objectCopy[key] = objectCopy[key].map((item: any) => {
        if (typeof item === "object") return deserializeDates(item);

        return item;
      });
    } else if (typeof objectCopy[key] === "object" && objectCopy[key] !== null) {
      if (objectCopy[key].__day__ !== undefined) objectCopy[key] = new Day(objectCopy[key].__day__);
      else if (objectCopy[key].__date__ !== undefined) objectCopy[key] = new Date(objectCopy[key].__date__);
      else objectCopy[key] = deserializeDates(objectCopy[key]);
    }
  }
  return objectCopy;
}

export function jsonEncodeFormValues(input: any): any {
  if (typeof input === "string") {
    return JSON.stringify(input);
  } else if (Array.isArray(input)) {
    return input.map((element) => jsonEncodeFormValues(element));
  } else if (typeof input === "object" && input !== null) {
    if (!isPlainObject(input)) return input;

    const transformedObj: { [key: string]: any } = {};
    for (const key in input)
      if (Object.prototype.hasOwnProperty.call(input, key)) transformedObj[key] = jsonEncodeFormValues(input[key]);

    return transformedObj;
  } else if (input === null || typeof input === "number" || typeof input === "boolean") {
    return JSON.stringify(input);
  }
}

export function serializeDates(object?: undefined): undefined;

export function serializeDates(object?: null): null;

export function serializeDates(object?: Record<string, any>): Record<string, any>;

export function serializeDates(object?: null | Record<string, any>): null | Record<string, any> | undefined {
  if (!isPlainObject(object)) return object;

  const objectCopy = { ...object };
  for (const key in objectCopy) {
    if (objectCopy[key] instanceof Date) {
      objectCopy[key] = { __date__: objectCopy[key].toISOString() };
    } else if (objectCopy[key] instanceof Day) {
      objectCopy[key] = { __day__: objectCopy[key].toString() };
    } else if (Array.isArray(objectCopy[key])) {
      objectCopy[key] = objectCopy[key].map((item: any) => {
        if (typeof item === "object") return serializeDates(item);

        return item;
      });
    } else if (typeof objectCopy[key] === "object" && objectCopy[key] !== null) {
      objectCopy[key] = serializeDates(objectCopy[key]);
    }
  }
  return objectCopy;
}

async function apiRequest<T extends keyof ApiRequests>(
  endpoint: T,
  data?: ApiRequests[T],
  canaryOnly?: boolean,
): Promise<ApiResponses[T]> {
  const parts = endpoint.split(".") as [keyof ServerPrefix, ...string[], VerbKeywords[keyof VerbKeywords]];
  const prefix = parts[0];
  const verb = parts[parts.length - 1] as VerbKeywords[keyof VerbKeywords];
  const requestId = uuid();

  data ??= {} as any;
  canaryOnly ??= sessionStorage.getItem("canary_only") === "true";
  const method = Object.entries(verbKeywords).find(([_, keywords]) => keywords.includes(verb))?.[0]!;

  if (serverPrefixes[prefix] !== "*") {
    const expectedHostname = getRouteBaseHostname(`${serverPrefixes[prefix]}.root`);
    if (location.hostname !== expectedHostname && !location.hostname.endsWith(`.${expectedHostname}`))
      throw new Error(`Endpoint ${endpoint} is not allowed to be called from ${location.origin}`);
  }
  const headers: RequestHeaders = {
    "X-Mfa-Code": mfaCode,
    "X-Request-Id": requestId,
  };

  let encodedData = serializeDates(data);

  if (containsFile(data)) {
    headers["Content-Type"] = "multipart/form-data";
    encodedData = jsonEncodeFormValues(encodedData);
  }

  if (REQUIRED_CAPTCHA_ENDPOINTS.includes(endpoint)) headers["X-Captcha-Token"] = await getRecaptchaToken(endpoint);

  const response = await axiosClient.request<ApiResponses[T]>({
    headers,
    method,
    [method === "get" ? "params" : "data"]: encodedData,
    url: `/api${canaryOnly ? "--canary-only" : ""}/call/${endpoint}`,
  });
  const [version, status, responseData] = [response.headers["x-app-version"], response.status, response.data] as const;
  const serverVersionDirection = getVersionDirection(version);

  if (status === 403 && (responseData as any).error === "admin:mfa_required")
    return handleMfaUnauthorizedResponse(endpoint, data as ApiRequests[T]);

  if (status === 403 && (responseData as { error?: "admin:mfa_setup_required" }).error === "admin:mfa_setup_required") {
    document.dispatchEvent(makeApiErrorEvent("mfa_setup_required"));
    return waitForever();
  }

  if (status === 403) {
    handleUnauthorizedResponse((responseData as any).requiredRole);
    return waitForever();
  }
  if (status === 402) {
    handlePaymentRequiredResponse(
      (responseData as any).subscriptionIssues,
      data && "teamId" in data ? (data.teamId as DataTypes["Teams"]["id"]) : undefined,
    );
    return waitForever();
  }
  if (rateLimitErrors.includes(status))
    return handleRateLimitResponse<T>(endpoint, data as ApiRequests[T], status, responseData);
  if (status === 500) return handleServerError<T>(endpoint, data as ApiRequests[T]);
  if (status === 501) {
    if (!canaryOnly) return apiRequest(endpoint, data, true);
    return handleServerError<T>(endpoint, data as ApiRequests[T]);
  }
  if (status === 413) {
    Sentry.captureException(new Error(`Size too large for request to ${endpoint}.`));
    document.dispatchEvent(makeApiErrorEvent("request_too_large"));
    return waitForever();
  }
  if (status === 422) {
    document.dispatchEvent(makeApiErrorEvent("captcha_failure"));
    return waitForever();
  }
  failingSince = undefined;
  if (serverVersionDirection === "newer") document.dispatchEvent(makeNewerApiVersionInteractionEvent());
  else if (serverVersionDirection === "older") document.dispatchEvent(makeOlderApiVersionInteractionEvent());
  if (status === 400 && (responseData as any).status === "unexpected_error") {
    if (serverVersionDirection === "older" && !canaryOnly) return apiRequest(endpoint, data, true);
    Sentry.captureException(new Error(`Unexpected response to ${endpoint}: ${(responseData as any).error}`));
    document.dispatchEvent(makeApiErrorEvent("unexpected"));
    return waitForever();
  }
  if (status === 400 && (responseData as any).status === "invalid_request") {
    if (serverVersionDirection === "older" && !canaryOnly) return apiRequest(endpoint, data, true);
    Sentry.captureException(
      new Error(`Invalid request to ${endpoint}: ${JSON.stringify((responseData as any).details)}`),
    );
    document.dispatchEvent(makeApiErrorEvent("unexpected"));
    return waitForever();
  }

  return ensureErrorChecked(endpoint, deserializeDates(responseData) as ApiResponses[T]);
}

function ensureErrorChecked<T extends keyof ApiResponses>(endpoint: T, response: ApiResponses[T]): ApiResponses[T] {
  if (response.success) return response;

  let errorChecked = false;
  const handler: ProxyHandler<ApiResponses[T]> = {
    get(target, prop, receiver) {
      if ((["status", "success", "error"] as const).includes(prop)) errorChecked = true;

      return Reflect.get(target, prop, receiver);
    },
    has(target, prop) {
      // catches `"error" in response`
      if (prop === "error") errorChecked = true;

      return Reflect.has(target, prop);
    },
  };

  setTimeout(() => {
    if (!errorChecked) {
      const error = `Bad Request error not checked in ${endpoint}: error is ${response.error}`;
      if (process.env.NODE_ENV === "development") alert(error);
      else Sentry.captureException(new Error(error));
    }
  }, 1_000);

  return new Proxy(response, handler);
}
function failingForTooLong(): boolean {
  return failingSince !== undefined && Date.now() - failingSince.getTime() > 60_000;
}
function getVersionDirection(version: string | undefined): "newer" | "older" | "same" {
  if (
    process.env.NODE_ENV !== "production" ||
    process.env.CURRENT_APP_VERSION === version ||
    !process.env.CURRENT_APP_VERSION ||
    !version
  )
    return "same";
  const direction = process.env.CURRENT_APP_VERSION.localeCompare(version, undefined, {
    numeric: true,
    sensitivity: "base",
  });
  if (direction === -1) return "newer";
  if (direction === 1) return "older";
  return "same";
}
async function handleMfaUnauthorizedResponse<T extends keyof ApiResponses>(
  endpoint: T,
  data: ApiRequests[T],
): Promise<ApiResponses[T] | never> {
  mfaCode = undefined;
  mfaCodePromise ??= new Promise((resolve) => {
    while (!mfaCode) mfaCode = presence(prompt("Enter MFA code"));

    resolve();
  });

  return (
    mfaCodePromise
      .finally(() => (mfaCodePromise = undefined))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
      .then(async () => api(endpoint as any, data as any)) as Promise<ApiResponses[T]>
  );
}

function handlePaymentRequiredResponse(subscriptionIssues: SubscriptionIssues, teamId?: DataTypes["Teams"]["id"]) {
  document.dispatchEvent(makeApiErrorEvent({ subscriptionIssues, teamId }));
}
async function handleRateLimitResponse<T extends keyof ApiResponses>(
  endpoint: T,
  data: ApiRequests[T],
  status: number,
  response?: unknown,
): Promise<ApiResponses[T]> {
  let retryIn = 5_000;
  failingSince ??= new Date();
  if (status !== 429 && failingForTooLong()) {
    Sentry.captureException(new Error(`Server unavailable (${status}) for over 60s at ${endpoint}.`));
    document.dispatchEvent(makeApiErrorEvent("unexpected"));
    return waitForever();
  }
  if (typeof response === "object" && response !== null && "resetsInMs" in response)
    retryIn = response.resetsInMs as number;

  console.log(`Hit rate limit, retrying in ${retryIn.toLocaleString()}ms`);
  return sleep(retryIn).then(async () => api(endpoint as any, data as any)) as Promise<ApiResponses[T]>;
}
async function handleServerError<T extends keyof ApiResponses>(
  endpoint: T,
  data: ApiRequests[T],
): Promise<ApiResponses[T]> {
  const retryIn = 5_000;
  failingSince ??= new Date();
  if (Date.now() - failingSince.getTime() > 30_000) {
    Sentry.captureException(new Error(`Unexpected server 500 at ${endpoint}.`));
    document.dispatchEvent(makeApiErrorEvent("unexpected"));
    return waitForever();
  }
  console.log(`Got server error, retrying in ${retryIn.toLocaleString()}ms`);
  return sleep(retryIn).then(async () => api(endpoint as any, data as any)) as Promise<ApiResponses[T]>;
}
function handleUnauthorizedResponse(requiredRole: EndpointAuth) {
  document.dispatchEvent(makeApiErrorEvent({ auth: requiredRole }));
}

function timezone(): null | string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
    // eslint-disable-next-line upscope/require-proper-error-handling
  } catch {
    return null;
  }
}

async function waitForever(): Promise<never> {
  return new Promise(() => {});
}
